/**
 * Shared Agent Console state: panel WebSocket/SSE, room selection, busy helpers.
 * LiveKit Room connection stays in Talk; leaveSession is registered there.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  fetchPanelSnapshot,
  fetchRoomParticipants,
  selectableAgents,
  subscribePanelUpdates,
  type PanelSnapshot,
  type RoomParticipant,
} from './livekitConnect'

const defaultBackendUrl = import.meta.env.VITE_DRIVEONAI_BACKEND_URL?.trim() ?? ''

const PARTICIPANT_POLL_MS = 3_000

type LeaveFn = () => Promise<void>

type PanelContextValue = {
  backendUrl: string
  setBackendUrl: (url: string) => void
  panel: PanelSnapshot | null
  panelError: string
  refreshPanel: () => Promise<void>
  selectedRoom: string
  setSelectedRoom: (name: string) => void
  onlineAgents: ReturnType<typeof selectableAgents>
  controlRoom: string
  connected: boolean
  setConnected: (v: boolean) => void
  connecting: boolean
  setConnecting: (v: boolean) => void
  activeRoom: string
  setActiveRoom: (name: string) => void
  roomParticipants: RoomParticipant[]
  refreshParticipants: (roomName: string) => Promise<void>
  busyKey: string | null
  runBusy: (key: string, fn: () => Promise<void>) => Promise<void>
  registerLeaveSession: (fn: LeaveFn | null) => void
  leaveSession: () => Promise<void>
}

const PanelContext = createContext<PanelContextValue | null>(null)

/** `networked=false` (public site): keep the shared call state but never fetch the admin panel. */
export function PanelProvider({ children, networked = true }: { children: ReactNode; networked?: boolean }) {
  const [backendUrl, setBackendUrl] = useState(defaultBackendUrl)
  const [selectedRoom, setSelectedRoom] = useState('')
  const [panel, setPanel] = useState<PanelSnapshot | null>(null)
  const [panelError, setPanelError] = useState('')
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [activeRoom, setActiveRoom] = useState('')
  const [roomParticipants, setRoomParticipants] = useState<RoomParticipant[]>(
    [],
  )
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const leaveRef = useRef<LeaveFn | null>(null)

  const controlRoom = (selectedRoom || activeRoom || '').trim()

  const applyPanelSnapshot = useCallback((snap: PanelSnapshot) => {
    setPanel(snap)
    setPanelError('')
    setSelectedRoom((prev) => {
      if (prev && snap.rooms.some((r) => r.name === prev)) return prev
      if (prev && !snap.rooms.some((r) => r.name === prev)) return ''
      return prev
    })
  }, [])

  const refreshPanel = useCallback(async () => {
    if (!networked) return
    try {
      const snap = await fetchPanelSnapshot(backendUrl)
      applyPanelSnapshot(snap)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setPanelError(message)
    }
  }, [backendUrl, applyPanelSnapshot, networked])

  const refreshParticipants = useCallback(
    async (roomName: string) => {
      if (!roomName || !networked) {
        setRoomParticipants([])
        return
      }
      try {
        const list = await fetchRoomParticipants(backendUrl, roomName)
        setRoomParticipants(list)
      } catch {
        setRoomParticipants([])
      }
    },
    [backendUrl, networked],
  )

  useEffect(() => {
    if (!networked) return
    const sub = subscribePanelUpdates(
      backendUrl,
      applyPanelSnapshot,
      (err) => setPanelError(err.message),
    )
    return () => sub.close()
  }, [backendUrl, applyPanelSnapshot, networked])

  useEffect(() => {
    if (!controlRoom) {
      setRoomParticipants([])
      return
    }
    void refreshParticipants(controlRoom)
    const id = window.setInterval(() => {
      void refreshParticipants(controlRoom)
    }, PARTICIPANT_POLL_MS)
    return () => window.clearInterval(id)
  }, [controlRoom, refreshParticipants])

  const runBusy = useCallback(
    async (key: string, fn: () => Promise<void>) => {
      if (busyKey) return
      setBusyKey(key)
      try {
        await fn()
      } finally {
        setBusyKey(null)
      }
    },
    [busyKey],
  )

  const registerLeaveSession = useCallback((fn: LeaveFn | null) => {
    leaveRef.current = fn
  }, [])

  const leaveSession = useCallback(async () => {
    if (leaveRef.current) {
      await leaveRef.current()
    }
  }, [])

  const onlineAgents = selectableAgents(panel?.agents)

  const value = useMemo(
    () => ({
      backendUrl,
      setBackendUrl,
      panel,
      panelError,
      refreshPanel,
      selectedRoom,
      setSelectedRoom,
      onlineAgents,
      controlRoom,
      connected,
      setConnected,
      connecting,
      setConnecting,
      activeRoom,
      setActiveRoom,
      roomParticipants,
      refreshParticipants,
      busyKey,
      runBusy,
      registerLeaveSession,
      leaveSession,
    }),
    [
      backendUrl,
      panel,
      panelError,
      refreshPanel,
      selectedRoom,
      onlineAgents,
      controlRoom,
      connected,
      connecting,
      activeRoom,
      roomParticipants,
      refreshParticipants,
      busyKey,
      runBusy,
      registerLeaveSession,
      leaveSession,
    ],
  )

  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  )
}

export function usePanel(): PanelContextValue {
  const ctx = useContext(PanelContext)
  if (!ctx) {
    throw new Error('usePanel must be used within PanelProvider')
  }
  return ctx
}
